/**
 * PublicLanding — the page that catches a stranger after the cinematic ends.
 *
 * Three CTAs in priority order:
 *   1. WALK A DEMO CHAIN — auth-free magic. Conversion moat.
 *   2. SIGN UP — for visitors who already understand and want in.
 *   3. WATCH INTRO AGAIN — for visitors who want to share the cold open with
 *      friends without dragging them through onboarding.
 *
 * Methodology hold #5: calm voice. No "JOIN NOW" desperation. The platform
 * states what it is and lets the stranger decide.
 *
 * Lead's voice: Midnite Productions. Honest. No marketing fluff. The
 * "Inspired by" credit visible as proof teachers are already trusting us.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, Play, ShieldCheck, FileCheck2, Sparkles, Globe } from 'lucide-react';
import { clearCinematicSeen as clearSeenCinematic } from './FounderOpening';  // eslint-disable-line no-unused-vars -- preserved for re-enable path
import api from '../lib/api';
import { apiBase } from '../lib/absoluteMediaUrl';
import ShareTSOGNRow from './ShareTSOGNRow';
import PageNarration from './PageNarration';

export default function PublicLanding() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/teacher/lessons/contest');
        setStats(r.data);
      } catch {/* graceful fallback */}
    })();
  }, []);

  // May 01 2026 per Lead: "Also remove intro video for now and link."
  // Previous `watchAgain` handler navigated to /intro?autoplay=1. Removed.
  // Re-enable path:
  //   const watchAgain = () => {
  //     clearSeenCinematic();
  //     navigate('/intro?autoplay=1');
  //   };
  // ...and restore the <button data-testid="footer-watch-again"> in the
  // footer, and the /intro route in App.js.

  // Apr 29 2026 follow-up #3: Lead's call: "narration should begin as
  // soon as a page starts." Browsers block <Audio>.play() outside a user
  // gesture, but the gesture FROM clicking BEGIN YOUR JOURNEY can be
  // carried over to /demo if we pre-construct + .play() the audio
  // synchronously inside the click handler and stash the live element
  // on `window`. The chain page's NarrationPlayButton picks it up on
  // mount and continues playback instead of constructing a new Audio.
  //
  // May 01 2026 production fix: use the resolved API base (current origin
  // in production, REACT_APP_BACKEND_URL only when running on localhost).
  // On the Emergent preview the two are the same so this worked; on
  // sharedorigins.org they diverge — calling the baked tsogn-demo URL
  // cross-origin with credentials is rejected by CORS. Going same-origin
  // sidesteps the entire dance.
  const beginJourney = () => {
    try {
      const url = `${apiBase()}/api/narration/chain/c929d883-96b7-426e-aa9c-e30a0b997b96/full`;
      const audio = new Audio(url);
      audio.preload = 'auto';
      audio.play().catch((e) => {
        console.warn('[PublicLanding] beginJourney pre-warm failed', e);
      });
      window.__tsogn_pending_narration = { url, audio };
    } catch (e) {
      console.warn('[PublicLanding] beginJourney error', e);
    }
    navigate('/demo');
  };

  return (
    <div className="min-h-screen text-white" style={{ background: '#0A0A0A' }} data-testid="public-landing">
      {/* Hero */}
      <section className="relative px-5 md:px-10 lg:px-20 pt-16 md:pt-24 pb-20 md:pb-28 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="font-mono text-[10px] md:text-xs text-white/45 tracking-[0.4em] uppercase" data-testid="landing-eyebrow">
              The Shared Origins Global Network · Presented by Midnite Productions
            </div>
            {/* Page narration — Apr 30 2026 iter_67 per Lead: "History
                walked through methodology page still not narrated why?"
                Added the gold Listen pill so visitors can hear the
                entire landing page read aloud by Midnite's voice clone. */}
            <PageNarration pageKey="landing" label="Listen · Intro" />
          </div>
          <h1 className="font-historical text-5xl md:text-7xl lg:text-8xl text-[#D4A84A] font-light tracking-tight leading-[1.05] mb-5" data-testid="landing-headline">
            History walked through<br /><span className="text-white">a methodology that doesn&apos;t blink.</span>
          </h1>
          <p className="font-mono text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed mb-3" data-testid="landing-subhead">
            Every claim is sourced. Every contradiction is shown.
          </p>
          <p className="font-mono text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed mb-3">
            Every link in the chain was placed there because the evidence earned it.
          </p>
          <p className="font-mono text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed mb-8">
            We don&apos;t narrate stories. We show receipts.
          </p>

          {/* CTA row — Apr 30 2026 iter_66 per Lead's brief.
              ORDER (left → right): START JOURNEY · SAMPLE CHAIN · WATCH INTRO.
              START JOURNEY routes to /login → after login lands on /world.
              SAMPLE CHAIN fires the auth-free First Walker demo. WATCH INTRO
              replays the founder cinematic. */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap" data-testid="landing-cta-row">
            {/* Apr 30 2026 iter_68 per Lead's repeated "start your journey
                button is blacked out" report: switched from Tailwind
                arbitrary-value classes to inline styles so nothing can
                override the gold fill + dark text. The button is now
                guaranteed to render matte-gold regardless of any
                inherited color-scheme / forced-colors rule. */}
            <button
              onClick={() => navigate('/login?next=/world')}
              data-testid="landing-start-journey-btn"
              className="inline-flex items-center justify-center gap-2 font-hud text-sm md:text-base tracking-widest transition-colors"
              style={{
                color: '#0A0A0A',
                backgroundColor: '#D4A84A',
                padding: '14px 28px',
                minHeight: '52px',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E5BC60'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#D4A84A'; }}
            >
              START YOUR JOURNEY
              <ArrowRight size={16} color="#0A0A0A" />
            </button>
            <button
              onClick={beginJourney}
              data-testid="landing-signup-btn"
              className="inline-flex items-center justify-center gap-2 font-hud text-sm md:text-base tracking-widest text-[#D4A84A] border-2 border-[#D4A84A]/65 bg-[#0A0A0A] px-6 py-3.5 min-h-[52px] hover:bg-[#D4A84A]/10 hover:border-[#D4A84A] transition-colors"
            >
              <Play size={14} /> SAMPLE CHAIN · EPISODE 1
            </button>
            {/* May 01 2026 per Lead: "Also remove intro video for now and
                link." Hero "Watch the intro" CTA removed alongside the
                /intro route. The Sample Chain button above is now the
                sole hero CTA. Re-enable path: restore the button + the
                watchAgain handler + the /intro route in App.js. */}
          </div>

          {/* SHARE TSOGN — horizontal icon row. Lead's call: "Put them as
              a horizontal row of icons, not text buttons. Cleaner visually."
              Apr 29 2026 follow-up #4: extracted into ShareTSOGNRow so the
              same row can be reused on /world (Time Machine). */}
          <ShareTSOGNRow />

          {/* Stripe transparency disclosure — Lead's call: judges respect honest mode flags */}
          <div className="mt-10 border-l-2 border-[#D4A84A]/40 pl-4 py-1.5 max-w-2xl" data-testid="landing-stripe-disclosure">
            <div className="font-hud text-xs md:text-sm tracking-widest uppercase text-[#D4A84A]/90 mb-1.5">
              Beta Pass · $20 · live when LLC clears
            </div>
            <p className="font-mono text-sm md:text-base text-white/70 leading-relaxed">
              Stripe is fully integrated and running in test mode while our LLC paperwork finishes.
              The flow is real — only the money is fenced. We don&apos;t fake completeness — we show you exactly where we are.
              <button
                onClick={() => navigate('/billing')}
                data-testid="landing-billing-link"
                className="ml-2 text-[#2F8A92] hover:text-[#D4A84A] underline-offset-2 hover:underline"
              >
                See the pass →
              </button>
            </p>
          </div>
        </div>

        {/* Subtle grain overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(212,168,74,0.4), transparent 50%), radial-gradient(circle at 80% 70%, rgba(47,138,146,0.3), transparent 50%)' }}
        />
      </section>

      {/* Methodology pillars */}
      <section className="px-5 md:px-10 lg:px-20 py-14 border-y border-[#D4A84A]/10" data-testid="landing-pillars">
        <div className="max-w-5xl mx-auto">
          <div className="font-mono text-xs md:text-sm text-[#D4A84A] tracking-[0.4em] uppercase mb-3">Five canonical methodology holds</div>
          <h2 className="font-historical text-3xl md:text-5xl text-white font-light leading-tight mb-10">
            What you can count on, every chain, every time.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: ShieldCheck, color: '#2F8A92', title: 'AI moderates behavior, not ideas',
                body: 'Our AI watches how people speak to each other, not what they choose to think. The methodology is for everyone.' },
              { icon: Sparkles, color: '#D4A84A', title: 'Strike decay is rehabilitative',
                body: 'Mistakes shouldn\'t define you forever. Strikes fade as you participate well — the platform forgives like a teacher does.' },
              { icon: FileCheck2, color: '#2F8A92', title: 'Your data is portable',
                body: 'Export everything you wrote, every time. You own your evidence log. We never hold your contributions hostage.' },
              { icon: ShieldCheck, color: '#FF6B6B', title: 'The platform defends you',
                body: 'Attackers get logged, documented, and responded to — out loud. We don\'t hide behind silent 403s.' },
              { icon: Globe, color: '#D4A84A', title: 'Calmer head than you came in with',
                body: 'No outrage UI. No engagement-bait. You leave a chain knowing more than you did, with the seams visible.' },
              { icon: GraduationCap, color: '#2F8A92', title: 'Honest-date principle',
                body: 'Empty states say "this is in production." Coming-soon means coming-soon. We don\'t fake completeness.' },
            ].map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="p-5 border border-white/10 rounded-md hover:border-white/25 transition-colors" data-testid={`landing-pillar-${i}`}>
                  <Icon size={20} style={{ color: p.color }} />
                  <h3 className="font-historical text-xl md:text-2xl text-white font-light mt-3 mb-2 leading-tight">{p.title}</h3>
                  <p className="font-mono text-sm md:text-base text-white/75 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* For Teachers — Apr 30 2026 iter_66 per Lead: full script
          inserted between the FOR TEACHERS eyebrow and the contest
          banner. Verbatim copy Lead supplied. */}
      <section className="px-5 md:px-10 lg:px-20 py-14" data-testid="landing-teachers">
        <div className="max-w-3xl mx-auto">
          <div className="font-mono text-xs md:text-sm text-[#D4A84A] tracking-[0.4em] uppercase mb-3">For teachers</div>
          <h3 className="font-historical text-3xl md:text-4xl lg:text-5xl text-white font-light leading-tight mb-6" data-testid="landing-teachers-headline">
            Tell us what you think.
          </h3>
          <div className="font-historical text-lg md:text-xl lg:text-2xl text-white/85 leading-relaxed space-y-4" data-testid="landing-teachers-script">
            <p>
              I am committed to making learning something that brings history to life. My goal is to make
              learning fun — not feel like a textbook.
            </p>
            <p>
              We all have different learning styles, and too often people get left behind. My goal is to
              take the excuses away from learning and put it all in one place — so you&rsquo;re not wasting
              valuable time hunting down sources. We want to make your life easier.
            </p>
            <p>
              My sister is a teacher. I wanted to be one too. Life took me down another path, but I never
              stopped caring about what good teaching can do for a kid.
            </p>
            <p>
              So I built this. To stop kids from falling through the cracks the way too many of us did.
            </p>
            <blockquote className="border-l-2 border-[#D4A84A] pl-5 my-7 italic text-[#D4A84A]" data-testid="landing-teachers-quote">
              &ldquo;Everybody is a genius. But if you judge a fish by its ability to climb a tree, it will
              live its whole life believing that it is stupid.&rdquo;
              <span className="block mt-2 text-base md:text-lg font-mono not-italic text-white/55 tracking-wide">
                — Often misattributed to Albert Einstein. Source unknown.
              </span>
            </blockquote>
            <p>
              That quote is why I&rsquo;m here. Every kid learns differently. Every kid deserves a chance
              to walk history themselves — not just be told what to memorize.
            </p>
            <p>
              Take a look around. Tell me what works. Tell me what doesn&rsquo;t.
            </p>
            <p className="text-[#D4A84A] font-historical text-xl md:text-2xl">
              Your voice can earn you full access.
            </p>
            <p>
              We run contests and reward suggestions that help us make the site better. If your idea gets
              used, you get a free membership. Simple as that. The people who help build this deserve to
              use it.
            </p>
            <p>
              Tell me what you see. Tell me what&rsquo;s missing. Tell me what would make this stronger
              for your classroom.
            </p>
            <p className="font-mono text-base md:text-lg text-white/65 mt-6">
              — Midnite
              <br />
              <span className="text-[#2F8A92]">Founder, Midnite Productions</span>
            </p>
          </div>
          {/* Contest banner — gold pill that closes out the teachers
              section. Sits BELOW the personal note from Midnite, above
              the founder block. */}
          <div className="mt-10 pt-8 border-t border-[#D4A84A]/22">
            <p className="font-historical text-2xl md:text-3xl text-white/95 font-light leading-relaxed mb-3" data-testid="landing-contest-headline">
              The first contest starts the day of our grand opening.
            </p>
            <p className="font-historical text-lg md:text-xl text-white/75 font-light leading-relaxed" data-testid="landing-teachers-body">
              All teacher beta members receive one month free, with the opportunity to earn a Lifetime
              Founders Membership through their contributions.
            </p>
          </div>
        </div>
      </section>

      {/* Founder note */}
      <section className="px-5 md:px-10 lg:px-20 py-14 border-t border-white/8" data-testid="landing-founder">
        <div className="max-w-3xl mx-auto">
          <div className="font-mono text-xs md:text-sm text-[#D4A84A] tracking-[0.4em] uppercase mb-3">A note from Midnite</div>
          <p className="font-historical text-2xl md:text-3xl text-white/90 font-light leading-relaxed mb-5">
            The platforms that already exist treat history as content. As something to feed an algorithm. We thought it deserved to be walked through carefully — with sources, contradictions, and the seams left visible.
          </p>
          <p className="font-mono text-base md:text-lg text-white/75 leading-relaxed mb-3">
            TSOGN is built around a simple idea: if we knew our history honestly, we&apos;d stop repeating it. The methodology is the contract. Every chain that ships meets the same gate. No exceptions.
          </p>
          <p className="font-mono text-base md:text-lg text-white/65 leading-relaxed">
            Built by Midnite Productions LLC. Aimed at billions of users — and at the one teacher, parent, or curious mind who walks the first chain tonight.
          </p>
        </div>
      </section>

      <footer className="px-5 md:px-10 lg:px-20 py-8 border-t border-white/10 font-mono text-xs md:text-sm text-white/45" data-testid="landing-footer">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-3 md:gap-6 justify-between items-start md:items-center">
          <div>© Midnite Productions LLC · The Shared Origins Global Network</div>
          <div className="flex gap-2">
            <button onClick={() => navigate('/login')} className="px-3 py-2 min-h-[40px] hover:text-white/90" data-testid="footer-login">Sign in</button>
            <button onClick={() => navigate('/signup')} className="px-3 py-2 min-h-[40px] hover:text-white/90" data-testid="footer-signup">Sign up</button>
            {/* May 01 2026 per Lead: "Also remove intro video for now and
                link." The "Watch intro" button + `watchAgain` handler +
                `clearSeenCinematic` import all removed. Re-enable path:
                restore the button below + the watchAgain handler above +
                the /intro route in App.js. */}
          </div>
        </div>
      </footer>
    </div>
  );
}
